/**
 * Palette recolouring — map a design onto a chosen set of colours.
 *
 * The obvious implementation (a CSS `hue-rotate` filter) is why the first version
 * of the palettes lied: hue-rotate is RELATIVE, so "purple, −90°" turned green
 * artwork orange and the swatch you clicked had nothing to do with the result.
 *
 * This maps colours ABSOLUTELY instead. Each palette is a light→dark ramp, and
 * every colour in the design is placed on that ramp by its own LIGHTNESS: paper
 * whites land on the lightest step, dark navies on the darkest, mid accents in the
 * middle. The design keeps its contrast and structure and genuinely becomes the
 * colour you picked.
 *
 * Reversible by construction: the original colours are snapshotted once per
 * element/frame in `paletteBase`, and every later palette is applied to that base,
 * never to the already-recoloured values. So switching palettes can't compound,
 * and Reset restores exactly what the designer drew.
 *
 * Not touched: raster images (their pixels aren't ours to rewrite) and shadow /
 * effect colours (recolouring a soft black shadow to purple reads as a bug, not a
 * palette).
 */
import { parseRgba } from '../panels/propertyControls';

/**
 * Style keys that hold a paintable colour, across every element type. Shadow and
 * effect colours are deliberately absent — see the note above.
 */
export const PALETTE_COLOR_KEYS = [
    'fill',            // shapes
    'stroke',          // shape outline
    'color',           // text / table text
    'backgroundColor', // text / image box fill
    'borderColor',
    'gradientColor1',
    'gradientColor2',
    'foreground',      // QR
    'background',      // QR / barcode fill
    'lineColor',       // barcode bars
    'headerBg',        // table
    'cellBg',
];

/** Frame keys that hold the artboard's own paint. */
export const PALETTE_FRAME_KEYS = [
    'background',
    'gradientColor1',
    'gradientColor2',
];

const EMPTY = new Set(['', 'transparent', 'none', 'inherit', 'currentcolor']);

const isPaintable = (v) => typeof v === 'string' && !EMPTY.has(v.trim().toLowerCase());

/**
 * Whites are left alone by every palette.
 *
 * Paper is white on purpose, and so is text or a logo reversed out of a dark panel —
 * tinting those to the palette's lightest step turned the card cream and washed the
 * reversed logo out until it barely read. Only NEUTRAL near-whites qualify: a pale
 * tint that carries real colour (say #fff3e6) is part of the design's palette and
 * still gets remapped.
 *
 * The test is "very light AND very close to neutral". Absolute channel spread is the
 * right measure here, not HSL saturation: at these lightnesses HSL saturation is
 * numerically unstable (a single level of difference reads as fully saturated), while
 * a spread of a few levels covers JPEG noise around white without letting a pale but
 * genuinely coloured tint through — #fff3e6 has a spread of 25 and still remaps.
 */
export const WHITE_LUMA = 0.93;    // 237/255 and up
export const WHITE_CHROMA = 0.03;  // max-min channel spread ≈ 8 levels

export function isNeutralWhite(rgb) {
    if (!rgb) return false;
    const { r, g, b } = rgb;
    const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    if (luma < WHITE_LUMA) return false;
    const chroma = (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
    return chroma <= WHITE_CHROMA;
}

const hex2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

/** Perceptual lightness in 0..1 (Rec. 709 luma — closer to how the eye ranks tones). */
export function colorLightness(color) {
    const c = parseRgba(color);
    if (!c) return null;
    return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
}

/**
 * Place one colour on the ramp, keeping its own alpha so semi-transparent tints
 * stay semi-transparent.
 */
export function remapColor(color, ramp = []) {
    if (!ramp.length || !isPaintable(color)) return color;
    const src = parseRgba(color);
    if (!src) return color;
    // White stays white — see isNeutralWhite.
    if (isNeutralWhite(src)) return color;
    const l = colorLightness(color);
    if (l == null) return color;
    // Ramp runs light → dark, so the lightest colour takes index 0.
    const idx = Math.max(0, Math.min(ramp.length - 1, Math.round((1 - l) * (ramp.length - 1))));
    const target = parseRgba(ramp[idx]);
    if (!target) return color;
    if (src.a >= 1) return `#${hex2(target.r)}${hex2(target.g)}${hex2(target.b)}`;
    return `#${hex2(target.r)}${hex2(target.g)}${hex2(target.b)}${hex2(src.a * 255)}`;
}

/** The subset of `keys` that `source` actually paints with — what gets snapshotted. */
function snapshot(source = {}, keys) {
    const out = {};
    keys.forEach((k) => {
        if (isPaintable(source[k])) out[k] = source[k];
    });
    return out;
}

/**
 * Recolour one element. Returns a patch for `updateElements`, or null when the
 * element has nothing remappable (an image, say).
 *
 * `paletteBase` is written on the first call and read on every later one, which is
 * what makes palette switching idempotent.
 */
export function recolorElementPatch(el, ramp) {
    if (!el) return null;
    const styles = el.styles || {};
    const base = el.paletteBase || snapshot(styles, PALETTE_COLOR_KEYS);
    const keys = Object.keys(base);
    // Text strokes are a LIST of { color } — they carry the outline colour on text.
    const baseStrokes = el.paletteBaseStrokes
        || (Array.isArray(styles.strokes) ? styles.strokes.map((s) => s?.color || null) : null);
    if (!keys.length && !baseStrokes?.length) return null;

    const nextStyles = {};
    keys.forEach((k) => { nextStyles[k] = remapColor(base[k], ramp); });
    if (baseStrokes?.length && Array.isArray(styles.strokes)) {
        nextStyles.strokes = styles.strokes.map((s, i) => (
            baseStrokes[i] ? { ...s, color: remapColor(baseStrokes[i], ramp) } : s
        ));
    }
    return {
        styles: nextStyles,
        paletteBase: base,
        ...(baseStrokes?.length ? { paletteBaseStrokes: baseStrokes } : null),
    };
}

/** Recolour one frame's own background. */
export function recolorFramePatch(frame, ramp) {
    if (!frame) return null;
    const base = frame.paletteBase || snapshot(frame, PALETTE_FRAME_KEYS);
    const patch = {};
    Object.keys(base).forEach((k) => { patch[k] = remapColor(base[k], ramp); });
    return { ...patch, paletteBase: base };
}

/** Undo a palette: put the snapshotted colours back and drop the snapshot. */
export function restoreElementPatch(el) {
    if (!el?.paletteBase && !el?.paletteBaseStrokes) return null;
    const styles = { ...(el.paletteBase || {}) };
    if (el.paletteBaseStrokes && Array.isArray(el.styles?.strokes)) {
        styles.strokes = el.styles.strokes.map((s, i) => (
            el.paletteBaseStrokes[i] ? { ...s, color: el.paletteBaseStrokes[i] } : s
        ));
    }
    return { styles, paletteBase: null, paletteBaseStrokes: null };
}

export function restoreFramePatch(frame) {
    if (!frame?.paletteBase) return null;
    return { ...frame.paletteBase, paletteBase: null, paletteId: null };
}
